import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import roomApi, {
  RoomDetail,
  RoomMatch,
  RoomMovie,
  SwipeResult,
} from "../../api/room";
import { ROUTES } from "../../constants/routes";
import styles from "./Room.module.scss";

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;
const STATUS_POLL_MS = 3000;

type Phase = "entry" | "lobby" | "swipe" | "results";

const RoomPage: React.FC = () => {
  const { code: urlCode } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [phase, setPhase] = useState<Phase>(urlCode ? "lobby" : "entry");
  const [room, setRoom] = useState<RoomDetail | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Entry
  const [joinCode, setJoinCode] = useState("");
  const [minRating, setMinRating] = useState("");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");

  // Lobby
  const [copied, setCopied] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Swipe
  const [movies, setMovies] = useState<RoomMovie[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [matches, setMatches] = useState<RoomMatch[]>([]);
  const [lastMatch, setLastMatch] = useState<SwipeResult | null>(null);
  const [swiping, setSwiping] = useState(false);

  const clearError = () => setError("");

  // ── Load room if URL has code ────────────────────────────
  useEffect(() => {
    if (urlCode) {
      loadRoom(urlCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlCode]);

  const loadRoom = async (code: string) => {
    setLoading(true);
    clearError();
    try {
      const { data } = await roomApi.getDetail(code);
      setRoom(data);
      if (data.status === "waiting") {
        setPhase("lobby");
      } else if (data.status === "active") {
        setPhase("swipe");
        fetchMovies(code);
        fetchMatches(code);
      } else {
        setPhase("results");
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load room");
      setPhase("entry");
    } finally {
      setLoading(false);
    }
  };

  // ── Create room ──────────────────────────────────────────
  const handleCreate = async () => {
    setLoading(true);
    clearError();
    try {
      const filters: any = {};
      if (minRating) filters.min_rating = parseFloat(minRating);
      if (yearFrom) filters.year_from = parseInt(yearFrom);
      if (yearTo) filters.year_to = parseInt(yearTo);

      const { data } = await roomApi.create(filters);
      setRoom(data);
      setPhase("lobby");
      navigate(`${ROUTES.ROOM}/${data.code}`, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to create room");
    } finally {
      setLoading(false);
    }
  };

  // ── Join room ────────────────────────────────────────────
  const handleJoin = async () => {
    if (!joinCode.trim()) return;
    setLoading(true);
    clearError();
    try {
      const { data } = await roomApi.join(joinCode.trim().toUpperCase());
      setRoom(data);
      setPhase("lobby");
      navigate(`${ROUTES.ROOM}/${data.code}`, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to join room");
    } finally {
      setLoading(false);
    }
  };

  // ── Lobby polling ────────────────────────────────────────
  useEffect(() => {
    if (phase === "lobby" && room) {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await roomApi.getStatus(room.code);
          setRoom((prev) =>
            prev
              ? {
                  ...prev,
                  status: data.status as RoomDetail["status"],
                  participants: data.participants,
                  matches: data.matches,
                }
              : prev
          );
          if (data.status === "active") {
            setPhase("swipe");
            fetchMovies(room.code);
          } else if (
            data.status === "completed" ||
            data.status === "expired" ||
            data.status === "cancelled"
          ) {
            setPhase("results");
          }
        } catch {
          // ignore polling errors
        }
      }, STATUS_POLL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, room?.code]);

  const handleStart = async () => {
    if (!room) return;
    setLoading(true);
    clearError();
    try {
      const { data } = await roomApi.start(room.code);
      setRoom(data);
      setPhase("swipe");
      fetchMovies(room.code);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Cannot start yet");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (!room) return;
    navigator.clipboard.writeText(room.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Swipe phase ──────────────────────────────────────────
  const fetchMovies = async (code: string) => {
    try {
      const { data } = await roomApi.getMovies(code);
      setMovies(data.results);
      setHasMore(data.has_more);
      setCurrentIdx(0);
    } catch {
      // ignore
    }
  };

  const fetchMatches = async (code: string) => {
    try {
      const { data } = await roomApi.getMatches(code);
      setMatches(data);
    } catch {
      // ignore
    }
  };

  const handleSwipe = useCallback(
    async (action: "like" | "dislike") => {
      if (!room || swiping || !movies[currentIdx]) return;
      setSwiping(true);
      setLastMatch(null);
      try {
        const movie = movies[currentIdx];
        const { data } = await roomApi.swipe(room.code, movie.id, action);

        if (data.match) {
          setLastMatch(data);
          fetchMatches(room.code);
        }

        const nextIdx = currentIdx + 1;
        if (nextIdx >= movies.length && hasMore) {
          // Fetch next batch
          const batch = await roomApi.getMovies(room.code);
          setMovies(batch.data.results);
          setHasMore(batch.data.has_more);
          setCurrentIdx(0);
        } else {
          setCurrentIdx(nextIdx);
        }
      } catch (err: any) {
        if (err.response?.data?.detail === "Already swiped on this movie.") {
          setCurrentIdx((prev) => prev + 1);
        }
      } finally {
        setSwiping(false);
      }
    },
    [room, swiping, movies, currentIdx, hasMore]
  );

  // Keyboard shortcuts for swipe
  useEffect(() => {
    if (phase !== "swipe") return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") handleSwipe("dislike");
      if (e.key === "ArrowRight") handleSwipe("like");
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [phase, handleSwipe]);

  // Poll for status updates during swipe to detect partner matches / room completion
  useEffect(() => {
    if (phase === "swipe" && room) {
      const interval = setInterval(async () => {
        try {
          const { data } = await roomApi.getStatus(room.code);
          if (
            data.status === "completed" ||
            data.status === "expired" ||
            data.status === "cancelled"
          ) {
            setRoom((prev) =>
              prev ? { ...prev, status: data.status as RoomDetail["status"] } : prev
            );
            setPhase("results");
          }
          if (data.matches.length > matches.length) {
            setMatches(data.matches);
          }
        } catch {
          // ignore
        }
      }, STATUS_POLL_MS);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, room?.code, matches.length]);

  const handleSelectMatch = async (matchId: number) => {
    if (!room) return;
    try {
      await roomApi.selectMatch(room.code, matchId);
      const { data } = await roomApi.getDetail(room.code);
      setRoom(data);
      setPhase("results");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to select match");
    }
  };

  const handleClose = async () => {
    if (!room) return;
    try {
      await roomApi.close(room.code);
      const { data } = await roomApi.getDetail(room.code);
      setRoom(data);
      setPhase("results");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to close room");
    }
  };

  const handleNewRoom = () => {
    setRoom(null);
    setPhase("entry");
    setMovies([]);
    setMatches([]);
    setCurrentIdx(0);
    setLastMatch(null);
    clearError();
    navigate(ROUTES.ROOM, { replace: true });
  };

  // ── Current user ID ──────────────────────────────────────
  // JWT payload parse to check if user is creator
  const getUserId = (): number | null => {
    const token = localStorage.getItem("accessToken");
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.user_id;
    } catch {
      return null;
    }
  };
  const userId = getUserId();
  const isCreator = room ? room.creator_id === userId : false;

  // ── Render ───────────────────────────────────────────────
  const currentMovie = movies[currentIdx] || null;
  const noMoviesLeft = !currentMovie && !hasMore;

  const selectedMatch = room?.matches?.find((m) => m.is_selected);

  return (
    <>
      <Header className={styles.headerWrapper} />
      <div className={styles.page}>
        {error && <div className={styles.error}>{error}</div>}

        {/* ── ENTRY PHASE ──────────────────────────────── */}
        {phase === "entry" && (
          <div className={styles.entry}>
            <div className={styles.title}>{t("room_title", "Movie Room")}</div>
            <div className={styles.subtitle}>
              {t(
                "room_subtitle",
                "Create a room and swipe on movies together with a friend"
              )}
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                {t("room_create", "Create a room")}
              </div>
              <div className={styles.filtersGrid}>
                <div className={styles.filterField}>
                  <label>{t("room_min_rating", "Min rating")}</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    step="0.5"
                    placeholder="e.g. 7.0"
                    value={minRating}
                    onChange={(e) => setMinRating(e.target.value)}
                  />
                </div>
                <div className={styles.filterField}>
                  <label>{t("room_year_from", "Year from")}</label>
                  <input
                    type="number"
                    min="1900"
                    max="2030"
                    placeholder="e.g. 2000"
                    value={yearFrom}
                    onChange={(e) => setYearFrom(e.target.value)}
                  />
                </div>
                <div className={styles.filterField}>
                  <label>{t("room_year_to", "Year to")}</label>
                  <input
                    type="number"
                    min="1900"
                    max="2030"
                    placeholder="e.g. 2024"
                    value={yearTo}
                    onChange={(e) => setYearTo(e.target.value)}
                  />
                </div>
              </div>
              <button
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleCreate}
                disabled={loading}
              >
                {loading
                  ? t("room_creating", "Creating...")
                  : t("room_create_btn", "Create Room")}
              </button>
            </div>

            <div className={styles.divider}>
              <span>{t("or", "or")}</span>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                {t("room_join", "Join a room")}
              </div>
              <div className={styles.joinRow}>
                <input
                  maxLength={6}
                  placeholder={t("room_code_placeholder", "Enter code")}
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && handleJoin()}
                />
                <button
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleJoin}
                  disabled={loading || !joinCode.trim()}
                >
                  {t("room_join_btn", "Join")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── LOBBY PHASE ──────────────────────────────── */}
        {phase === "lobby" && room && (
          <div className={styles.lobby}>
            <div className={styles.title}>{t("room_lobby", "Waiting Room")}</div>

            <div className={styles.roomCode}>
              <span className={styles.codeLabel}>
                {t("room_share_code", "Share this code with your friend")}
              </span>
              <span className={styles.codeValue} onClick={handleCopyCode}>
                {room.code}
              </span>
              <span className={styles.codeCopied}>
                {copied ? t("room_copied", "Copied!") : ""}
              </span>
            </div>

            <div className={styles.participantsList}>
              {room.participants.map((p) => (
                <div key={p.user_id} className={styles.participant}>
                  <span
                    className={`${styles.dot} ${
                      !p.is_active ? styles.dotInactive : ""
                    }`}
                  />
                  <span className={styles.participantName}>{p.username}</span>
                  {p.user_id === room.creator_id && (
                    <span className={styles.creatorBadge}>
                      {t("room_host", "Host")}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {room.participants.length < 2 && (
              <div className={styles.waitingText}>
                {t(
                  "room_waiting",
                  "Waiting for another person to join"
                )}
                <span className={styles.waitingDots} />
              </div>
            )}

            <div className={styles.lobbyActions}>
              {isCreator && room.participants.length >= 2 && (
                <button
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleStart}
                  disabled={loading}
                >
                  {t("room_start", "Start Swiping")}
                </button>
              )}
              {isCreator && (
                <button
                  className={`${styles.btn} ${styles.btnDanger}`}
                  onClick={handleClose}
                >
                  {t("room_cancel", "Cancel Room")}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── SWIPE PHASE ──────────────────────────────── */}
        {phase === "swipe" && room && (
          <div className={styles.swipeArea}>
            {lastMatch && (
              <div className={styles.matchBanner}>
                <div className={styles.matchTitle}>
                  {t("room_its_a_match", "It's a Match!")}
                </div>
                <div className={styles.matchMovie}>{lastMatch.movie_title}</div>
              </div>
            )}

            {currentMovie ? (
              <>
                <div className={styles.swipeCard}>
                  {currentMovie.poster_path ? (
                    <img
                      src={`${IMG_BASE}${currentMovie.poster_path}`}
                      alt={currentMovie.title}
                    />
                  ) : (
                    <div className={styles.loading}>{currentMovie.title}</div>
                  )}
                  <div className={styles.cardOverlay}>
                    <div className={styles.cardTitle}>{currentMovie.title}</div>
                    <div className={styles.cardMeta}>
                      {currentMovie.release_date && (
                        <span className={styles.cardYear}>
                          {currentMovie.release_date.slice(0, 4)}
                        </span>
                      )}
                      {(currentMovie.imdb_rating || currentMovie.vote_average) && (
                        <span className={styles.cardRating}>
                          {currentMovie.imdb_rating?.toFixed(1) ||
                            currentMovie.vote_average?.toFixed(1)}
                        </span>
                      )}
                    </div>
                    {currentMovie.genres && (
                      <div className={styles.cardGenres}>{currentMovie.genres}</div>
                    )}
                    {currentMovie.overview && (
                      <div className={styles.cardOverview}>
                        {currentMovie.overview}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.swipeButtons}>
                  <button
                    className={`${styles.swipeBtn} ${styles.swipeBtnDislike}`}
                    onClick={() => handleSwipe("dislike")}
                    disabled={swiping}
                    aria-label="Dislike"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                  <button
                    className={`${styles.swipeBtn} ${styles.swipeBtnLike}`}
                    onClick={() => handleSwipe("like")}
                    disabled={swiping}
                    aria-label="Like"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                  </button>
                </div>
              </>
            ) : noMoviesLeft ? (
              <div className={styles.noMoviesLeft}>
                <div className={styles.noMoviesText}>
                  {t("room_no_more_movies", "No more movies to swipe on")}
                </div>
              </div>
            ) : (
              <div className={styles.loading}>
                {t("room_loading_movies", "Loading movies...")}
              </div>
            )}

            {/* Matches sidebar */}
            {matches.length > 0 && (
              <div className={styles.matchesSidebar}>
                <div className={styles.matchesSidebarTitle}>
                  {t("room_matches", "Matches")} ({matches.length})
                </div>
                {matches.map((m) => (
                  <div key={m.id} className={styles.matchItem}>
                    {m.movie_poster_path && (
                      <img
                        src={`${IMG_BASE}${m.movie_poster_path}`}
                        alt={m.movie_title}
                      />
                    )}
                    <span className={styles.matchItemTitle}>
                      {m.movie_title}
                    </span>
                    {!m.is_selected && (
                      <button
                        className={styles.selectBtn}
                        onClick={() => handleSelectMatch(m.id)}
                      >
                        {t("room_select", "Select")}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Close room button for creator */}
            {isCreator && (
              <button
                className={`${styles.btn} ${styles.btnDanger}`}
                onClick={handleClose}
                style={{ marginTop: 20 }}
              >
                {t("room_end_session", "End Session")}
              </button>
            )}
          </div>
        )}

        {/* ── RESULTS PHASE ────────────────────────────── */}
        {phase === "results" && room && (
          <div className={styles.results}>
            <div className={styles.title}>
              {room.status === "completed"
                ? t("room_completed", "Movie Selected!")
                : room.status === "expired"
                ? t("room_expired", "Room Expired")
                : t("room_cancelled", "Room Cancelled")}
            </div>

            <span
              className={`${styles.statusBadge} ${
                room.status === "completed"
                  ? styles.statusCompleted
                  : room.status === "expired"
                  ? styles.statusExpired
                  : styles.statusCancelled
              }`}
            >
              {room.status}
            </span>

            {selectedMatch && (
              <div className={styles.resultCard}>
                {selectedMatch.movie_poster_path && (
                  <img
                    src={`${IMG_BASE}${selectedMatch.movie_poster_path}`}
                    alt={selectedMatch.movie_title}
                  />
                )}
                <div className={styles.resultTitle}>
                  {selectedMatch.movie_title}
                </div>
                <div className={styles.resultSubtitle}>
                  {t("room_you_picked", "You both picked this one!")}
                </div>
              </div>
            )}

            {room.matches && room.matches.length > 0 && (
              <div className={styles.allMatches}>
                <div className={styles.allMatchesTitle}>
                  {t("room_all_matches", "All Matches")} ({room.matches.length})
                </div>
                {room.matches.map((m) => (
                  <div key={m.id} className={styles.matchItem}>
                    {m.movie_poster_path && (
                      <img
                        src={`${IMG_BASE}${m.movie_poster_path}`}
                        alt={m.movie_title}
                      />
                    )}
                    <span className={styles.matchItemTitle}>
                      {m.movie_title}
                    </span>
                    {m.is_selected && (
                      <span className={`${styles.statusBadge} ${styles.statusCompleted}`}>
                        {t("room_selected", "Selected")}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleNewRoom}
            >
              {t("room_new_room", "Create New Room")}
            </button>
          </div>
        )}

        {loading && phase !== "entry" && (
          <div className={styles.loading}>
            {t("room_loading", "Loading...")}
          </div>
        )}
      </div>
      <Footer source="" />
    </>
  );
};

export default RoomPage;
